/*
 * Copyright (c) 2026 Bluemate B.V.
 *
 * The push guard hint: a single line explaining why push cannot work in this
 * context (insecure origin, unsupported browser, mobile browser outside the
 * installed app, a suppressed permission prompt). Extracted from
 * PushNotificationsCard so it can render at the top of the device list section
 * (under its header) instead of in the card body. Reads PushChannelsContext, so
 * it can be dropped anywhere inside the provider; renders nothing when no guard
 * applies.
 */

import './style/pushNotifications.scss';

import React, {useContext} from 'react';

import {useTranslation} from 'react-i18next';

import {AwesomeIcon} from '@dvrd/dvr-controls';

import {PushChannelsContext} from './pushChannelsContext';

import {showPushPermissionGuide} from './pushPermissionGuide';

/** True when the page is NOT a secure context (served over plain http, and not
 *  localhost). Service workers and PushManager are only exposed in a secure
 *  context, so on an http deployment detectSupport() reports "unsupported" even
 *  on a fully capable browser — the real cause is the unencrypted connection,
 *  not the browser. */
function isInsecureContext(): boolean {
    return typeof window !== 'undefined' && window.isSecureContext === false;
}

/** The current URL upgraded to https, so the user can reopen the app over an
 *  encrypted connection. Null when there's nothing to upgrade (protocol is not
 *  plain http). */
function getSecureAppUrl(): string | null {
    if (typeof window === 'undefined') return null;
    if (window.location.protocol !== 'http:') return null;
    return window.location.href.replace(/^http:/i, 'https:');
}

export default function PushGuardHint() {
    const {t} = useTranslation();
    const pushContext = useContext(PushChannelsContext);
    if (!pushContext) return null;
    const {state} = pushContext;

    // Read the LIVE permission, not the (possibly stale) provider state — the
    // user may grant notifications from the browser settings and come back
    // without a refresh. Notification.permission is always current.
    const livePermission: NotificationPermission =
        typeof Notification !== 'undefined' ? Notification.permission : state.permission;
    const isGranted = livePermission === 'granted';

    // Shown when we deliberately stopped re-firing the native prompt because
    // the user dismissed/ignored it (Chrome auto-blocks the origin after a
    // few ignored prompts — see usePushSubscription). Softer copy than a
    // "blocked" guide: nothing is broken yet, the browser just won't show
    // the prompt again automatically, so we point to the settings path.
    function openSuppressedHelp() {
        showPushPermissionGuide(t, 'suppressed');
    }

    if (!state.isSupported) {
        // An http (non-secure) origin doesn't expose service workers /
        // PushManager, so detectSupport() reports "unsupported" even on a
        // fully capable browser. Explain the real cause (no encrypted
        // connection) and offer to reopen the app over https in a new
        // window, instead of the misleading "browser doesn't support" text.
        if (isInsecureContext()) {
            const secureUrl = getSecureAppUrl();
            return (
                <div className='push-card-hint push-card-hint-warn'>
                    <AwesomeIcon name='triangle-exclamation' />
                    <span>
                        {t(
                            'push.insecureContext',
                            'Push-meldingen werken alleen via een beveiligde (versleutelde) https-verbinding. Via een gewone http-verbinding kan Bluemate meldingen niet correct inschakelen.'
                        )}
                        {secureUrl && (
                            <>
                                {' '}
                                <a
                                    className='push-secure-link'
                                    href={secureUrl}
                                    target='_blank'
                                    rel='noopener noreferrer'
                                >
                                    {t('push.openSecure', 'Open Bluemate via https in een nieuw venster')}
                                </a>
                            </>
                        )}
                    </span>
                </div>
            );
        }
        // Generic unsupported — desktop-only in practice. EVERY mobile
        // browser has canUseHere false (including all iOS browsers,
        // where PushManager doesn't exist outside the home-screen app)
        // and falls through to the canUseHere guard below, so the whole
        // mobile spectrum shows one identical install hint. How to get
        // the app installed (Safari-only on iOS, etc.) is the install
        // banners' job (pwaBanners), not this card's.
        if (state.canUseHere) {
            return (
                <div className='push-card-hint push-card-hint-warn'>
                    <AwesomeIcon name='circle-info' />
                    <span>{t('push.unsupported', 'Je browser ondersteunt geen push-meldingen.')}</span>
                </div>
            );
        }
    }
    // No "notifications live in the app" line here on purpose: this hint sits
    // inside the admin push-subscriptions section, where the rule is already
    // stated where it is actionable — the channels select's "dit apparaat
    // (alleen in de app)" option and the add-notification wizard.
    //
    // Show ONLY as a transient reaction to a just-dismissed prompt
    // (state.error, cleared by the next refresh) — never from the
    // persistent promptSuppressed back-off, which used to keep this guide
    // (and the perceived block) on screen for days after one dismissal.
    // Gated on !isGranted: once granted (incl. from settings) there is
    // nothing left to enable.
    if (!isGranted && state.error === 'prompt-suppressed') {
        return (
            <div
                className='push-card-hint push-card-hint-warn push-card-hint-clickable'
                role='button'
                tabIndex={0}
                onClick={openSuppressedHelp}
                onKeyDown={e => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        openSuppressedHelp();
                    }
                }}
            >
                <AwesomeIcon name='triangle-exclamation' />
                <span>
                    {t(
                        'push.suppressedHint',
                        'We tonen de meldingsvraag niet opnieuw. Tik hier om te zien hoe je meldingen inschakelt.'
                    )}
                </span>
            </div>
        );
    }
    if (state.error) {
        return (
            <div className='push-card-hint push-card-hint-warn'>
                <AwesomeIcon name='triangle-exclamation' />
                <span>
                    {t(
                        `push.errors.${state.error}`,
                        t(
                            'push.errors.unexpected',
                            'Er is iets misgegaan met pushmeldingen. Probeer het later opnieuw.'
                        )
                    )}
                </span>
            </div>
        );
    }
    return null;
}
